// Frontend view of the backend country registry (GET /api/countries, backed by
// config_lookup rows `country_config:<ISO2>` in ooe-service — see
// ooe-service/utils/country-registry.js).
//
// Onboarding a country is a backend data change only. FALLBACK mirrors the
// backend's built-in defaults and is used if the fetch fails, so the customer
// payment pages keep working for the existing countries when the endpoint is
// unreachable.
import {client} from './api-client'

const intl = (iso, name) => ({
  iso,
  name,
  status: 'flag_gated',
  usdOnly: true,
  addressProvider: 'loqate',
  paymentMethods: ['card'],
})

const FALLBACK = [
  {iso: 'US', name: 'United States', paymentMethods: ['card', 'ach']},
  {iso: 'CA', name: 'Canada', paymentMethods: ['card']},
  {iso: 'AU', name: 'Australia', paymentMethods: ['card', 'becs']},
  {iso: 'NZ', name: 'New Zealand', paymentMethods: ['card']},
  {iso: 'GB', name: 'Great Britain', paymentMethods: ['card']},
  {iso: 'KY', name: 'Cayman Islands', paymentMethods: ['card']},
  {iso: 'BB', name: 'Barbados', paymentMethods: ['card']},
  intl('ES', 'Spain'),
  intl('IT', 'Italy'),
  intl('IN', 'India'),
  intl('PH', 'Philippines'),
  intl('VN', 'Vietnam'),
  intl('GR', 'Greece'),
  intl('BR', 'Brazil'),
  intl('HR', 'Croatia'),
  intl('DO', 'Dominican Republic'),
  intl('EC', 'Ecuador'),
  intl('DE', 'Germany'),
  intl('IM', 'Isle of Man'),
  intl('MY', 'Malaysia'),
  intl('MT', 'Malta'),
  intl('MX', 'Mexico'),
  intl('NL', 'Netherlands'),
  intl('RO', 'Romania'),
  intl('SG', 'Singapore'),
  intl('ZA', 'South Africa'),
  intl('SE', 'Sweden'),
]

const normalize = c => ({
  ...c,
  iso: String(c.iso).toUpperCase(),
  name: c.name || c.iso,
  usdOnly: c.usdOnly === true,
  addressProvider: c.addressProvider === 'loqate' ? 'loqate' : 'google',
  paymentMethods:
    Array.isArray(c.paymentMethods) && c.paymentMethods.length
      ? c.paymentMethods
      : ['card'],
})

let registry = FALLBACK.map(normalize)
const listeners = new Set()

export const getCountryRegistry = () => registry

export const getRegistryCountry = code =>
  registry.find(c => c.iso === String(code || '').toUpperCase())

export const setCountryRegistry = list => {
  registry = list
    .filter(c => c && c.iso && c.status !== 'disabled')
    .map(normalize)
  listeners.forEach(listener => listener())
}

export const subscribeToCountryRegistry = listener => {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

// Fetches the registry once; silently keeps FALLBACK on any failure. The app
// shell waits for this before rendering, because the payment forms look the
// customer's country up in COUNTRIES during their first render.
export async function loadCountryRegistry() {
  try {
    const response = await client('countries')
    if (Array.isArray(response?.countries) && response.countries.length) {
      setCountryRegistry(response.countries)
    }
  } catch (err) {
    console.error('Failed to fetch country registry, using defaults', err)
  }
}

// Customer payment methods for a country, e.g. ['card', 'ach'].
export const getPaymentMethodValues = code =>
  getRegistryCountry(code)?.paymentMethods || ['card']

// International (USD-billed) markets: show the currency-conversion notice and
// the localized address labels.
export const isUsdOnlyCountry = code =>
  getRegistryCountry(code)?.usdOnly === true

// BUSREQ-14451: every registry country except US/CA (domestic) and AU/NZ (which keep
// their dedicated ABN/NZBN field) gets one optional, unvalidated Tax ID / VAT / GST
// field. It rides on the same `abn` form/payload key the backend already forwards to
// Recurly's vatNumber.
const NON_INTERNATIONAL_TAX_ID_COUNTRIES = ['US', 'CA', 'AU', 'NZ']

export const isInternationalTaxIdCountry = code => {
  const iso = String(code || '').toUpperCase()
  return (
    !!iso &&
    !NON_INTERNATIONAL_TAX_ID_COUNTRIES.includes(iso) &&
    !!getRegistryCountry(iso)
  )
}

// AU/NZ numbers are stored without spaces; international tax IDs are kept as typed
// (no country-specific format rules), only trimmed.
export const normalizeTaxIdentifier = (country, value) =>
  ['AU', 'NZ'].includes(String(country || '').toUpperCase())
    ? String(value || '').replaceAll(' ', '')
    : String(value || '').trim()

export const isLoqateCountry = code =>
  getRegistryCountry(code)?.addressProvider === 'loqate'

// Countries a customer may switch between on the order-details page: US, CA
// and the international USD-billed group.
export const isSwitchableCountry = code =>
  code === 'US' || code === 'CA' || isUsdOnlyCountry(code)

export const getSwitchableCountries = () =>
  registry
    .filter(c => isSwitchableCountry(c.iso))
    .map(c => ({name: c.name, isoValue: c.iso}))
